import { useLayoutEffect, useRef, useState } from 'react';

// Room kept for the "Labels" dropdown that replaces the chips when they do not
// all fit (BoardLabelsFilter, owner 13.10.2026).
const MORE_CHIP_WIDTH = 64;

const px = (v) => parseFloat(v) || 0;
const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Keeps the filter bar on ONE row (Main artboard): when the board's label
 * chips do not fit beside the fixed chips and the "N of M shown" block, the
 * INACTIVE ones that do not fit are reported as folded, in palette order
 * from the end — like Linear's filter row. Active labels always stay visible
 * (their ✕ is how you remove them).
 *
 * Widths are read from the live chips (`data-filter-label`) and cached, so a
 * folded chip is put back as soon as its cached width fits again. Without
 * layout (jsdom) every width is 0 and nothing ever folds.
 *
 * Returns the ids to fold. `labels` = [{ id, on }] in palette order.
 */
export default function useLabelChipOverflow({ barRef, leftRef, rightRef, labels }) {
  const widths = useRef(new Map());
  const [folded, setFolded] = useState([]);
  const [, setTick] = useState(0);

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setTick((t) => t + 1));
    ro.observe(bar);
    return () => ro.disconnect();
  }, [barRef]);

  // Every render, on purpose (any chip, count or the right-hand block can
  // change width): cheap DOM reads, and the state only changes when the
  // folded set does, so this settles after at most one extra pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const bar = barRef.current;
    const left = leftRef.current;
    if (!bar || !left) return;
    left.querySelectorAll('[data-filter-label]').forEach((el) => {
      if (el.offsetWidth) widths.current.set(el.getAttribute('data-filter-label'), el.offsetWidth);
    });
    const barStyle = getComputedStyle(bar);
    const right = rightRef?.current;
    const avail =
      bar.clientWidth - px(barStyle.paddingLeft) - px(barStyle.paddingRight) - (right ? right.offsetWidth + px(barStyle.columnGap) : 0);
    const gap = px(getComputedStyle(left).columnGap);
    let used = 0;
    for (const el of left.children) {
      if (el.hasAttribute('data-filter-label') || el.classList.contains('board-filter-more')) continue;
      used += el.offsetWidth + gap;
    }
    const w = (id) => (widths.current.get(id) || 0) + gap;
    labels.filter((l) => l.on).forEach((l) => {
      used += w(l.id);
    });
    const inactive = labels.filter((l) => !l.on);
    const all = inactive.reduce((sum, l) => sum + w(l.id), used);
    let next = [];
    if (avail > 0 && all > avail) {
      let budget = avail - MORE_CHIP_WIDTH - gap - used;
      for (const l of inactive) {
        if (next.length === 0 && w(l.id) <= budget) budget -= w(l.id);
        else next.push(l.id);
      }
    }
    if (!sameIds(next, folded)) setFolded(next);
  });

  return folded;
}
